// src/components/block/liveStreamManagment/LiveExportModal.tsx
// Boîte de dialogue du bouton Live régie QUAND le multiview est actif.
//
// Même principe que RecordingSessionModal pour le multi-angle : la grille multiview de
// la régie n'est pas forcément ce qu'on veut exposer au public — certains angles ne
// servent qu'à l'écran de contrôle. On demande donc, comme pour Rec, lesquels des
// angles actuellement dans la grille doivent être publiés sous {publicRef}_ang{N}.
//
// Toutes les sources sont cochées par défaut (comme Rec) : décocher restreint, ne pas
// toucher exporte tout ce qui est dans la grille.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Radio, VideoOff } from 'lucide-react';

import Toggle from '../../widget/inputs/Toggle';
import { RecordingSessionSource } from './RecordingSessionModal';

export interface LiveExportModalProps {
  /** Angles actuellement occupés dans la grille multiview. */
  sources           : RecordingSessionSource[];
  /** Sélection reprise d'une confirmation précédente (réouverture) — défaut : tout coché. */
  initialSelectedIds?: Set<string>;
  onCancel          : () => void;
  onStart           : (selectedIds: Set<string>) => void;
}

const LiveExportModal: React.FC<LiveExportModalProps> = ({
  sources, initialSelectedIds, onCancel, onStart,
}) => {
  // Même logique que RecordingSessionModal : on mémorise les DÉcochées, pas les
  // cochées — une source ajoutée à la grille pendant que la fenêtre est ouverte
  // arrive donc cochée.
  const [disabledIds, setDisabledIds] = useState<Set<string>>(() => {
    if (!initialSelectedIds) return new Set();
    return new Set(sources.map(s => s.id).filter(id => !initialSelectedIds.has(id)));
  });

  const toggleSource = (id: string, on: boolean) => {
    setDisabledIds(prev => {
      const next = new Set(prev);
      if (on) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleStart = () => {
    onStart(new Set(sources.filter(s => !disabledIds.has(s.id)).map(s => s.id)));
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') handleStart();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  return ReactDOM.createPortal(
    <div style={S.overlay}>
      <div onClick={e => e.stopPropagation()} style={S.modal}>

        <div style={S.header}>
          <span style={S.title}>Flux exposés au public</span>
          <button onClick={onCancel} style={S.closeIcon} title="Fermer">
            <X style={{ width: 16, height: 16 }} />
          </button>
        </div>

        <div style={S.body}>
          <div style={S.hint}>
            La chaîne est en multiview : choisissez lesquels de ces angles sont publiés
            publiquement (le programme composé reste diffusé quoi qu'il arrive).
          </div>

          {sources.length === 0 ? (
            <div style={S.empty}>
              <VideoOff style={{ width: 16, height: 16, flexShrink: 0 }} />
              <span>Aucun angle dans la grille multiview</span>
            </div>
          ) : (
            <div style={S.sourceList}>
              {sources.map(source => {
                const on = !disabledIds.has(source.id);
                return (
                  <div key={source.id} style={S.sourceRow}>
                    <Toggle
                      form             = "switch"
                      checked          = {on}
                      internalPosition = {false}
                      width            = "28px"
                      height           = "16px"
                      fillColorOn      = "#25C400"
                      fillColorOff     = "#334155"
                      onChange         = {isOn => toggleSource(source.id, isOn)}
                    />
                    <span style={S.sourceName}>{source.label}</span>
                    {source.mediamtxPath && (
                      <span style={S.atSource} title="Déjà un flux MediaMTX existant : rien à republier, seule sa lecture publique est ouverte">
                        déjà en ligne
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={S.footer}>
          <button onClick={onCancel} style={S.cancelBtn}>Annuler</button>
          <button onClick={handleStart} style={S.startBtn}>
            <Radio style={{ width: 15, height: 15, flexShrink: 0 }} />
            Confirmer
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default LiveExportModal;

// ─── Styles ───────────────────────────────────────────────────────────────────
// Repris de RecordingSessionModal — même langage visuel pour les deux boîtes de la
// régie.

const S = {
  overlay: {
    position      : 'fixed',
    inset         : 0,
    zIndex        : 9999,
    background    : 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(4px)',
    display       : 'flex',
    alignItems    : 'center',
    justifyContent: 'center',
    padding       : 24,
  } as React.CSSProperties,

  modal: {
    background   : 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
    border       : '1px solid #1e293b',
    borderRadius : 14,
    width        : '100%',
    maxWidth     : 560,
    maxHeight    : '88vh',
    boxShadow    : '0 24px 64px rgba(0,0,0,0.6)',
    overflow     : 'hidden',
    display      : 'flex',
    flexDirection: 'column',
  } as React.CSSProperties,

  header: {
    position    : 'relative',
    padding     : '16px 44px',
    borderBottom: '1px solid #1e293b',
    flexShrink  : 0,
    textAlign   : 'center',
  } as React.CSSProperties,

  title: {
    fontSize  : 16,
    fontWeight: 700,
    color     : '#ffffff',
  } as React.CSSProperties,

  closeIcon: {
    position    : 'absolute',
    top         : 12,
    right       : 12,
    background  : 'none',
    border      : 'none',
    cursor      : 'pointer',
    color       : '#ffffff',
    display     : 'flex',
    padding     : 4,
    borderRadius: 6,
  } as React.CSSProperties,

  body: {
    padding      : '18px 20px',
    display      : 'flex',
    flexDirection: 'column',
    gap          : 10,
    overflowY    : 'auto',
  } as React.CSSProperties,

  hint: {
    fontSize  : 12,
    color     : '#94a3b8',
    marginBottom: 4,
  } as React.CSSProperties,

  sourceList: {
    display      : 'flex',
    flexDirection: 'column',
    maxHeight    : 320,
    overflowY    : 'auto',
  } as React.CSSProperties,

  sourceRow: {
    display     : 'flex',
    alignItems  : 'center',
    gap         : 18,
    padding     : '6px 2px',
    borderBottom: '1px solid rgba(255,255,255,0.04)',
  } as React.CSSProperties,

  sourceName: {
    fontSize    : 14,
    color       : '#ffffff',
    flex        : 1,
    minWidth    : 0,
    overflow    : 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace  : 'nowrap',
  } as React.CSSProperties,

  atSource: {
    marginLeft  : 'auto',
    fontSize    : 10,
    fontWeight  : 700,
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color       : '#4ade80',
    border      : '1px solid rgba(74,222,128,0.35)',
    borderRadius: 5,
    padding     : '2px 6px',
    whiteSpace  : 'nowrap',
    flexShrink  : 0,
  } as React.CSSProperties,

  empty: {
    display   : 'flex',
    alignItems: 'center',
    gap       : 8,
    padding   : '10px 0',
    fontSize  : 13,
    color     : '#94a3b8',
  } as React.CSSProperties,

  footer: {
    padding       : '12px 20px',
    borderTop     : '1px solid #0f172a',
    background    : 'rgba(0,0,0,0.2)',
    display       : 'flex',
    justifyContent: 'flex-end',
    gap           : 10,
    flexShrink    : 0,
  } as React.CSSProperties,

  cancelBtn: {
    background  : 'rgba(255,255,255,0.05)',
    border      : '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 13,
    fontWeight  : 600,
    padding     : '8px 18px',
    cursor      : 'pointer',
  } as React.CSSProperties,

  startBtn: {
    background  : '#dc2626',
    border      : '1px solid #dc2626',
    borderRadius: 7,
    color       : '#ffffff',
    fontSize    : 13,
    fontWeight  : 700,
    padding     : '8px 18px',
    display     : 'flex',
    alignItems  : 'center',
    gap         : 7,
    cursor      : 'pointer',
  } as React.CSSProperties,
};
